import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa'
import { useSwipeable } from 'react-swipeable'

type SwipeTilesProps = {
  backgroundColor?: 'linen' | 'aquaSpring' | 'frostWhite'
  tiles: Array<JSX.Element>
  className?: string
}

export const SwipeTiles = ({
  tiles = [],
  backgroundColor = 'linen',
  className = ''
}: SwipeTilesProps) => {
  const [selectedTile, setSelectedTile] = useState(0)
  const next = useCallback(() => {
    setSelectedTile(c => (c < tiles.length - 1 ? c + 1 : c))
  }, [tiles])

  const previous = () => {
    setSelectedTile(c => (c > 0 ? c - 1 : c))
  }

  const { ref, ...handlers } = useSwipeable({
    onSwipedLeft: next,
    onSwipedRight: previous,
    delta: 10,
    trackTouch: true,
    trackMouse: true
  })

  const backgroundStyles = useMemo(() => {
    let backgroundClasses = ''
    switch (backgroundColor) {
      case 'aquaSpring':
        backgroundClasses = `${backgroundClasses} bg-aquaSpring`
        break
      case 'frostWhite':
        backgroundClasses = `${backgroundClasses} bg-frostWhite`
        break
      case 'linen':
      default:
        backgroundClasses = `${backgroundClasses} bg-linen`
        break
    }
    return backgroundClasses
  }, [backgroundColor])

  const tileContainerRef = useRef<HTMLDivElement>()

  const refPassThrough = (el: HTMLDivElement) => {
    ref(el)
    tileContainerRef.current = el
  }

  const tileRefs = useRef<HTMLDivElement[]>([])

  useEffect(() => {
    if (!tileContainerRef.current || !tileRefs.current[selectedTile]) {
      return
    }

    tileRefs.current[selectedTile]?.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'center'
    })
  }, [selectedTile])

  return (
    <div
      {...handlers}
      ref={refPassThrough}
      className={`w-full ${backgroundStyles} md:bg-transparent rounded-[5px] p-5 md:p-0 flex flex-col gap-[12px] md:gap-5`}
    >
      <div
        className={`overflow-hidden no-scrollbar w-full flex flex-row gap-[10px] justify-stretch md:justify-between  ${className}`}
      >
        {tiles.map((tile, idx) => (
          <div
            ref={el => {
              tileRefs.current[idx] = el!
            }}
            className={`flex flex-col items-start md:${backgroundStyles} md:rounded-[5px] gap-[12px] md:gap-4 md:p-4 p-0 overflow-clip transition-all duration-300 flex-[0_0_100%] md:flex-1`}
            key={idx}
          >
            {tile}
          </div>
        ))}
      </div>
      <div className='flex flex-row justify-between md:hidden items-center mt-[10px]'>
        <FaChevronLeft
          onClick={previous}
          className={`${
            selectedTile === 0 ? 'text-avatarBorderGrey' : 'text-forestDeep'
          }`}
        />
        <div className='flex gap-2 flex-row justify-center'>
          {tiles.map((_, idx) => (
            <div
              key={`key_${idx}`}
              className={`h-2 ${
                idx === selectedTile ? 'w-5' : 'w-2'
              } bg-forestDeep rounded-full transition-all duration-200`}
            />
          ))}
        </div>
        <FaChevronRight
          onClick={next}
          className={`${
            selectedTile === tiles.length - 1
              ? 'text-avatarBorderGrey'
              : 'text-forestDeep'
          }`}
        />
      </div>
    </div>
  )
}

type DonateImpactTile = {
  icon: string
  title: string
  description: string
}

type DonateImpactTilesProps = {
  items: DonateImpactTile[]
}

export const DonateImpactTiles = ({ items = [] }: DonateImpactTilesProps) => {
  const tiles = items.map(tile => {
    return (
      <>
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img src={tile.icon} alt='' aria-hidden className='h-[30px] shrink-0' />
        <div className='flex flex-col gap-[5px]'>
          <span className='text-[14px] leading-[20px] font-semibold text-forestDeep'>
            {tile.title}
          </span>
          <span className='text-[14px] leading-5 font-medium text-forestDeep'>
            {tile.description}
          </span>
        </div>
      </>
    )
  })

  return <SwipeTiles tiles={tiles} />
}
